import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Search, Layers } from "lucide-react";
import { http } from "@/lib/api";
import { fmtMoney, flagEmoji } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StreamManageModal } from "./StreamManageModal";
import { GeoOverridesDialog } from "./GeoOverridesDialog";

const numOrEmpty = (v) => (v === null || v === undefined ? "" : String(v));
const cleanNum = (v) => v.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");

// Unified override matrix (same rules as Offer → Users):
// key -> { field on row, active flag, dynamic flag, default field, percent? }
const COLS = [
  { key: "cpa_rev", label: "CPA Rev", field: "cpa_revenue_override", active: (r) => r.cpa_active, dynamic: (r) => r.cpa_dynamic, def: "default_cpa_revenue", staffOnly: true },
  { key: "cpa_pay", label: "CPA Payout", field: "cpa_payout_override", active: (r) => r.cpa_active, dynamic: () => false, def: "default_cpa_payout" },
  { key: "rs_adv", label: "RS ADV %", field: "rs_adv_percent_override", active: (r) => r.rs_active, dynamic: () => false, def: "default_rs_adv_percent", staffOnly: true, percent: true },
  { key: "rs_pay", label: "RS Payout %", field: "rs_payout_override", active: (r) => r.rs_active, dynamic: () => false, def: "default_rs_percent", percent: true },
];

function OverrideInput({ value, defaultValue, percent, onChange, testId }) {
  const inherited = value === "" || value === null || value === undefined;
  return (
    <div className="flex flex-col gap-0.5">
      <Input value={value} onChange={(e) => onChange(cleanNum(e.target.value))} placeholder={numOrEmpty(defaultValue) || "—"}
        data-testid={testId} className="h-8 w-20 border-white/[0.1] bg-[#0A0A0A] text-right font-mono text-xs text-foreground" />
      <span className="text-[10px] text-muted-foreground/70">
        {inherited ? "Inherited" : (percent ? `${Number(value) || 0}%` : fmtMoney(value))}
      </span>
    </div>
  );
}

export function OfferRatesTab({ userId, userName, isStaff }) {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [edits, setEdits] = useState({});
  const [geoEdits, setGeoEdits] = useState({});
  const [geoDlg, setGeoDlg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["user-offer-rates", userId],
    queryFn: async () => (await http.get(`/offers/user-rates/${userId}`)).data,
    enabled: !!userId,
  });
  const rows = data?.items || [];
  const staff = isStaff && (data?.is_staff ?? true);
  const cols = COLS.filter((c) => !c.staffOnly || staff);

  const getEdit = (r, col) => {
    const e = edits[r.offer_id];
    if (e && col.key in e) return e[col.key];
    return numOrEmpty(r[col.field]);
  };
  const setEdit = (offerId, key, val) =>
    setEdits((p) => ({ ...p, [offerId]: { ...p[offerId], [key]: val } }));

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter === "active" && !r.active_streams) return false;
      if (filter === "inactive" && r.active_streams) return false;
      if (!q) return true;
      return [`#${r.offer_seq}`, r.offer_name, r.geo, r.advertiser_name, r.payment_model]
        .some((v) => String(v || "").toLowerCase().includes(q));
    });
  }, [rows, search, filter]);

  const editable = (r, col) => col.active(r) && !col.dynamic(r);

  const save = async () => {
    const items = rows.map((r) => {
      const it = { offer_id: r.offer_id };
      for (const col of cols) {
        if (editable(r, col)) {
          const key = { cpa_rev: "cpa_revenue_override", cpa_pay: "cpa_payout_override", rs_adv: "rs_adv_percent_override", rs_pay: "rs_payout_override" }[col.key];
          it[key] = getEdit(r, col);
        }
      }
      if (r.offer_id in geoEdits) it.geo_overrides = geoEdits[r.offer_id];
      return it;
    });
    for (const it of items) {
      for (const [k, v] of Object.entries(it)) {
        if (k === "offer_id" || k === "geo_overrides") continue;
        if (v !== "" && (isNaN(Number(v)) || Number(v) < 0)) return toast.error("Rates must be non-negative numbers");
      }
    }
    setSaving(true);
    try {
      const res = await http.put(`/offers/user-rates/${userId}`, { items });
      toast.success(`Saved ${res.data.saved} override row(s)`);
      setEdits({});
      setGeoEdits({});
      qc.invalidateQueries({ queryKey: ["user-offer-rates", userId] });
      qc.invalidateQueries({ queryKey: ["streams"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const hasChanges = Object.keys(edits).length > 0 || Object.keys(geoEdits).length > 0;
  const th = "px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70";
  const td = "px-3 py-2 text-sm";

  if (isLoading) return <p className="py-8 text-center text-sm text-muted-foreground">Loading rates…</p>;

  return (
    <div className="space-y-4" data-testid="offer-rates-tab">
      {geoDlg && (
        <GeoOverridesDialog open={!!geoDlg} onClose={() => setGeoDlg(null)} geos={geoDlg.geos || []}
          value={geoEdits[geoDlg.offer_id] ?? geoDlg.geo_overrides ?? {}}
          onApply={(m) => setGeoEdits((p) => ({ ...p, [geoDlg.offer_id]: m }))}
          percent={geoDlg.payment_model === "RevShare"} staff={staff}
          dynamicRevenue={geoDlg.cpa_dynamic || (geoDlg.payment_model === "RevShare" && geoDlg.rs_dynamic)}
          geoRates={geoDlg.geo_rates || {}}
          defaults={{ payout: geoDlg.default_cpa_payout ?? geoDlg.default_rs_percent, revenue: geoDlg.default_cpa_revenue }} />
      )}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search offer, GEO, advertiser, model…"
            data-testid="offer-rates-search" className="h-9 border-white/[0.1] bg-[#0A0A0A] pl-9 text-sm" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="h-9 w-[190px] border-white/[0.1] bg-[#0A0A0A] text-sm" data-testid="offer-rates-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="border-white/[0.1] bg-[#1a1a1e]">
            <SelectItem value="all">All streams</SelectItem>
            <SelectItem value="active">Has active streams</SelectItem>
            <SelectItem value="inactive">No active streams</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={save} disabled={!hasChanges || saving} data-testid="offer-rates-save" className="ml-auto h-9">
          {saving ? "Saving…" : "Save rates"}
        </Button>
      </div>

      {filtered.length === 0 ? (
        <p className="rounded-xl border border-white/[0.08] bg-[#0A0A0A] py-10 text-center text-sm text-muted-foreground">
          {rows.length === 0 ? "This user has no streams on any offer yet." : "No offers match your filters."}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-white/[0.08]">
          <table className="w-full min-w-[820px]">
            <thead className="border-b border-white/[0.08] bg-white/[0.02]">
              <tr>
                <th className={th}>Offer</th>
                {cols.map((c) => <th key={c.key} className={`${th} text-right`}>{c.label}</th>)}
                <th className={th}>Streams</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.05]">
              {filtered.map((r) => (
                <tr key={r.offer_id} data-testid={`offer-rate-row-${r.offer_id}`} className="hover:bg-white/[0.02]">
                  <td className={td}>
                    <div className="font-medium text-foreground">#{r.offer_seq} {r.offer_name}</div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                      <span>{flagEmoji(r.geo)} {r.geo}</span>
                      {r.payment_model && <span className="rounded border border-white/[0.1] px-1.5 py-px">{r.payment_model}</span>}
                      {(r.geos || []).length > 1 && (
                        <button type="button" onClick={() => setGeoDlg(r)} data-testid={`geo-ovr-open-${r.offer_id}`}
                          className={`rounded border px-1.5 py-px transition-colors hover:border-white/30 ${Object.keys(geoEdits[r.offer_id] ?? r.geo_overrides ?? {}).length ? "border-[#22C55E]/40 text-[#22C55E]" : "border-white/[0.1] text-muted-foreground"}`}>
                          GEO rates{Object.keys(geoEdits[r.offer_id] ?? r.geo_overrides ?? {}).length ? ` · ${Object.keys(geoEdits[r.offer_id] ?? r.geo_overrides ?? {}).length}` : ""}
                        </button>
                      )}
                      {staff && r.advertiser_name && <span>· {r.advertiser_name}</span>}
                    </div>
                  </td>
                  {cols.map((c) => (
                    <td key={c.key} className={`${td} text-right`}>
                      {!c.active(r) ? (
                        <span className="text-xs text-muted-foreground/40" data-testid={`${c.key}-na-${r.offer_id}`}>—</span>
                      ) : c.dynamic(r) ? (
                        <span className="text-xs font-medium text-[#F59E0B]" data-testid={`${c.key}-dynamic-${r.offer_id}`}>Dynamic</span>
                      ) : (
                        <div className="inline-flex flex-col items-end gap-0.5">
                          <div className="text-[11px] text-muted-foreground/70">def {c.percent ? `${Number(r[c.def]) || 0}%` : fmtMoney(r[c.def])}</div>
                          <OverrideInput value={getEdit(r, c)} defaultValue={r[c.def]} percent={c.percent}
                            onChange={(v) => setEdit(r.offer_id, c.key, v)} testId={`${c.key}-override-${r.offer_id}`} />
                        </div>
                      )}
                    </td>
                  ))}
                  <td className={td}>
                    <button onClick={() => setModal({ offerId: r.offer_id, label: `#${r.offer_seq} ${r.offer_name} — ${r.geo}` })}
                      data-testid={`offer-rate-streams-${r.offer_id}`}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-white/[0.1] bg-white/[0.03] px-2.5 py-1 text-xs text-foreground hover:bg-white/[0.07]">
                      <Layers className="h-3.5 w-3.5" />
                      <span className="font-mono">{r.streams_count}</span>
                      <span className="text-muted-foreground">total ·</span>
                      <span className="font-mono text-[#22C55E]">{r.active_streams}</span>
                      <span className="text-muted-foreground">active</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <StreamManageModal userId={userId} userName={userName} offerId={modal.offerId} offerLabel={modal.label}
          open={!!modal} onClose={() => setModal(null)}
          onChanged={() => { qc.invalidateQueries({ queryKey: ["user-offer-rates", userId] }); qc.invalidateQueries({ queryKey: ["streams"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); }} />
      )}
    </div>
  );
}
